import React, { useState } from 'react';
import { translate } from 'react-jhipster';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAppSelector } from 'app/config/store';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { IRebateSchedule, FIELDS_BY_FREQUENCY, browserTimeZone, periodLabel, timeZoneLabel } from './schedule-section';
import { IRebateReportSetting } from './report-section';
import { IRebateDocumentAttachSetting } from './document-section';
import { RebateDocumentView } from 'app/modules/reports/rebate-report/rebate-document-view';

// Maps the stored letter category value to its i18n label key, same options as letter-section.
const LETTER_LABEL_KEYS: Record<string, string> = {
  REBATE_SHMOKH: 'rebateShmokh',
  REBATE_BANK_A: 'rebateBankA',
  REBATE_THIRD_PARTY: 'rebateThirdParty',
  REBATE_BANK_A_THIRD_PARTY: 'rebateBankAThirdParty',
  REBATE_CANCELLATION: 'rebateCancellation',
};

// Mirrors RebateLetterCategoryResolver.resolveDocumentType on the backend.
const DOC_TYPE_BY_LETTER_CATEGORY: Record<string, 'invoice' | 'incentive' | 'cancellation'> = {
  REBATE_SHMOKH: 'invoice',
  REBATE_BANK_A: 'incentive',
  REBATE_THIRD_PARTY: 'incentive',
  REBATE_BANK_A_THIRD_PARTY: 'incentive',
  REBATE_CANCELLATION: 'cancellation',
};

interface PreviewSectionProps {
  schedule: IRebateSchedule;
  emails: string[];
  letterEnabled: boolean;
  letterCategory: string;
  letterLanguage: string;
  reportSetting: IRebateReportSetting;
  documentSetting: IRebateDocumentAttachSetting;
  bankName: string;
  settingName?: string;
  overpaymentWarning?: boolean;
  rebateSettingId?: number | string;
  onGenerateReport?: () => void;
  generatingReport?: boolean;
}

type DialogKind = 'email' | 'letter' | null;

export const PreviewSection = ({
  schedule,
  emails,
  letterEnabled,
  letterCategory,
  letterLanguage,
  reportSetting,
  documentSetting,
  bankName,
  settingName,
  overpaymentWarning,
  rebateSettingId,
  onGenerateReport,
  generatingReport,
}: PreviewSectionProps) => {
  const currentLocale = useAppSelector(state => state.locale.currentLocale);
  const localDay = dayjs().locale(currentLocale || 'en');

  const [dialogKind, setDialogKind] = useState<DialogKind>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState<{ subject: string; html: string } | undefined>(undefined);
  // One entry per letter the run would produce (ALSHMOKH-66) — length 1 for Invoice/Bank A/
  // Cancellation, length N for Third Party, N+1 for Bank A + Third Party. An empty array is a
  // valid, successful response (every recipient's share is zero this period) — letterLoadFailed
  // is what actually distinguishes a request failure (ALSHMOKH-68).
  const [letterPreview, setLetterPreview] = useState<any[] | undefined>(undefined);
  const [letterLoadFailed, setLetterLoadFailed] = useState(false);

  const docType = DOC_TYPE_BY_LETTER_CATEGORY[letterCategory];
  const canPreviewLetter = Boolean(rebateSettingId && letterEnabled && docType);

  const closeDialog = () => {
    setDialogKind(null);
    setEmailPreview(undefined);
    setLetterPreview(undefined);
    setLetterLoadFailed(false);
  };

  const openEmailPreview = async () => {
    if (!canPreviewLetter) {
      return;
    }
    setDialogKind('email');
    setDialogLoading(true);
    try {
      const { data } = await axios.get(`api/rebate-documents/preview-email?rebateSettingId=${rebateSettingId}&docType=${docType}`);
      setEmailPreview(data);
    } catch (error) {
      setEmailPreview({ subject: '', html: translate('comexApp.bank.rebate.preview.loadError') });
    } finally {
      setDialogLoading(false);
    }
  };

  const openLetterPreview = async () => {
    if (!canPreviewLetter) {
      return;
    }
    setDialogKind('letter');
    setDialogLoading(true);
    setLetterLoadFailed(false);
    try {
      const { data } = await axios.get(`api/rebate-documents/preview?rebateSettingId=${rebateSettingId}&docType=${docType}`);
      setLetterPreview(data);
    } catch (error) {
      setLetterPreview(undefined);
      setLetterLoadFailed(true);
    } finally {
      setDialogLoading(false);
    }
  };

  const scheduleFieldValue = (field: keyof IRebateSchedule) => {
    switch (field) {
      case 'dayOfWeek':
        return localDay.day(Number(schedule.dayOfWeek)).format('dddd');
      case 'month':
        return localDay.month(Number(schedule.month) - 1).format('MMMM');
      case 'period':
        return periodLabel(localDay, schedule);
      case 'time':
        return `${schedule.time} ${timeZoneLabel(browserTimeZone())}`;
      default:
        return schedule[field];
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <Grid container spacing={1} sx={{ mb: 0.75 }}>
      <Grid item xs={5} sm={4}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Grid>
      <Grid item xs={7} sm={8}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
      </Grid>
    </Grid>
  );

  const block = (titleKey: string, content: React.ReactNode, onClick?: () => void) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
      }}
      onClick={onClick}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {translate(`comexApp.bank.rebate.sections.${titleKey}`)}
        {onClick && (
          <Typography component="span" variant="caption" color="primary" sx={{ ml: 1, fontWeight: 400 }}>
            {translate('comexApp.bank.rebate.preview.clickToView')}
          </Typography>
        )}
      </Typography>
      {content}
    </Paper>
  );

  const chipList = (items: React.ReactNode[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items}
    </Box>
  );

  return (
    <Box>
      {overpaymentWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('comexApp.bank.rebate.preview.overpaymentWarning')}
        </Alert>
      )}
      {settingName && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('comexApp.bank.rebate.setting.label')}: {settingName}
        </Typography>
      )}
      {block('schedule', schedule.frequency ? (
        <>
          {row(translate('comexApp.bank.rebate.schedule.frequency'), translate(`comexApp.bank.rebate.schedule.frequencyOptions.${schedule.frequency.toLowerCase()}`))}
          {FIELDS_BY_FREQUENCY[schedule.frequency].map(field => (
            <React.Fragment key={field}>
              {row(translate(`comexApp.bank.rebate.schedule.${field}`), scheduleFieldValue(field))}
            </React.Fragment>
          ))}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">{translate('comexApp.bank.rebate.schedule.select')}</Typography>
      ))}

      {block(
        'email',
        emails.length > 0 ? (
          chipList(emails.map(email => <Chip key={email} label={email} size="small" />))
        ) : (
          <Typography variant="body2" color="text.secondary">{translate('comexApp.bank.rebate.email.empty')}</Typography>
        ),
        emails.length > 0 && canPreviewLetter ? () => { openEmailPreview(); } : undefined
      )}

      {block(
        'letter',
        letterEnabled ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>
              {letterCategory ? translate(`comexApp.bank.rebate.letter.options.${LETTER_LABEL_KEYS[letterCategory]}`, { bankName }) : '-'}
            </Typography>
            {row(
              translate('comexApp.bank.rebate.letter.languageLabel'),
              translate(`comexApp.bank.rebate.letter.language.${letterLanguage === 'AR' ? 'arabic' : 'english'}`)
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">{translate('comexApp.bank.rebate.letter.disabledHint')}</Typography>
        ),
        canPreviewLetter ? () => { openLetterPreview(); } : undefined
      )}

      {block(
        'reports',
        reportSetting.enabled ? (
          chipList(reportSetting.columns.map(column => (
            <Chip key={column} label={translate(`comexApp.bank.rebate.report.columns.${column}`, { bankName })} size="small" />
          )))
        ) : (
          <Typography variant="body2" color="text.secondary">{translate('comexApp.bank.rebate.report.disabledHint')}</Typography>
        ),
        reportSetting.enabled && onGenerateReport && !generatingReport ? onGenerateReport : undefined
      )}

      {block('document', documentSetting.enabled ? (
        chipList(documentSetting.documents.map(document => (
          <Chip key={document} label={translate(`comexApp.bank.rebate.document.documents.${document}`)} size="small" />
        )))
      ) : (
        <Typography variant="body2" color="text.secondary">{translate('comexApp.bank.rebate.document.disabledHint')}</Typography>
      ))}
      {documentSetting.enabled && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1.5, mb: 2 }}>
          {translate('comexApp.bank.rebate.document.previewUnavailable')}
        </Typography>
      )}

      <Dialog open={dialogKind !== null} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {dialogKind === 'email'
            ? translate('comexApp.bank.rebate.preview.emailDialogTitle')
            : translate('comexApp.bank.rebate.preview.letterDialogTitle')}
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : dialogKind === 'email' && emailPreview ? (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {emailPreview.subject}
              </Typography>
              <iframe
                title="email-preview"
                srcDoc={emailPreview.html}
                sandbox=""
                style={{ width: '100%', height: 480, border: 'none' }}
              />
            </>
          ) : dialogKind === 'letter' && letterPreview && letterPreview.length > 0 ? (
            <>
              {letterPreview.map((letter, index) => (
                <Box key={`${letter.beneficiaryName}-${index}`} sx={{ mb: index < letterPreview.length - 1 ? 3 : 0 }}>
                  {letterPreview.length > 1 && (
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {translate('comexApp.bank.rebate.preview.letterOfCount', {
                        current: index + 1,
                        total: letterPreview.length,
                        beneficiaryName: letter.beneficiaryName,
                      })}
                    </Typography>
                  )}
                  <RebateDocumentView
                    language={letter.language === 'AR' ? 'ar' : 'en'}
                    docType={docType}
                    referenceNo={letter.referenceNo}
                    date={letter.date}
                    recipientName={letter.bankName}
                    beneficiaryName={letter.beneficiaryName}
                    accountNumber={letter.accountNumber}
                    totalDeals={letter.totalDeals}
                    amount={letter.amount}
                    currency={letter.currency}
                    month={letter.periodLabel}
                  />
                  {letterPreview.length > 1 && index < letterPreview.length - 1 && <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 2 }} />}
                </Box>
              ))}
            </>
          ) : dialogKind === 'letter' && !letterLoadFailed ? (
            <Typography variant="body2" color="text.secondary">
              {translate('comexApp.bank.rebate.preview.noLetterThisPeriod')}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {translate('comexApp.bank.rebate.preview.loadError')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PreviewSection;
